import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { FormGroup } from '@/components/form/FormGroup';
import { Input } from '@/components/form/Input';
import { Select } from '@/components/form/Select';
import { Textarea } from '@/components/form/Textarea';
import { useToast } from '@/components/ui/useToast';
import { useDocViewer } from '@/components/ui/useDocViewer';
import {
  cancelInvoice,
  getInvoice,
  issueInvoice,
  markInvoicePaid,
} from '@/api/salesInvoice';
import { InvoiceAttachments } from './InvoiceAttachments';
import { InvoiceEditPanel } from './InvoiceEditPanel';
import { useAuth } from '@/auth/useAuth';
import { formatDate, formatINR } from '@/lib/format';
import { apiError } from '@/lib/apiError';

interface Props {
  open: boolean;
  invoiceId: number | null;
  onClose: () => void;
  /** Open straight into the edit or cancel form (the list's Edit / Cancel buttons). */
  startWith?: 'edit' | 'cancel';
}

export function InvoiceDetailModal({ open, invoiceId, onClose: closeModal, startWith }: Props) {
  const qc = useQueryClient();
  const toast = useToast();
  const doc = useDocViewer();
  const canEdit = useAuth().hasPerm('acc-invoice-edit');
  const [showPay, setShowPay] = useState(false);
  const [showCancel, setShowCancel] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  useEffect(() => {
    if (!open) return;
    setShowEdit(startWith === 'edit' && canEdit);
    setShowCancel(startWith === 'cancel');
  }, [open, invoiceId, startWith, canEdit]);
  const onClose = () => {
    setShowEdit(false);
    closeModal();
  };
  const [payAmt, setPayAmt] = useState('');
  const [payMode, setPayMode] = useState('NEFT');
  const [payRef, setPayRef] = useState('');
  const [cancelReason, setCancelReason] = useState('');

  const query = useQuery({
    queryKey: ['sales-invoice', invoiceId],
    queryFn: () => getInvoice(invoiceId as number),
    enabled: open && !!invoiceId,
  });
  const inv = query.data ?? null;

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['sales-invoices'] });
    qc.invalidateQueries({ queryKey: ['sales-invoice', invoiceId] });
    qc.invalidateQueries({ queryKey: ['invoice-logs'] });
  };

  const handleErr = (e: unknown) => {
    toast.push(apiError(e, 'Failed'), 'error');
  };

  const issueMut = useMutation({
    mutationFn: () => issueInvoice(invoiceId as number),
    onSuccess: (r) => {
      toast.pushStep(`Invoice ${r.invoiceNo} issued`, {
        body: 'Next step — raise the Gate Pass for outbound dispatch.',
        href: '/sales/gate-pass',
        nextLabel: 'Open Gate Pass',
        tag: `invoice-issued-${r.id}`,
      });
      invalidate();
    },
    onError: handleErr,
  });
  const payMut = useMutation({
    mutationFn: () =>
      markInvoicePaid(invoiceId as number, {
        amount: Number(payAmt),
        mode: payMode,
        ref: payRef || undefined,
      }),
    onSuccess: (r) => {
      toast.push(`Payment recorded — status ${r.status}`, 'success');
      setShowPay(false);
      setPayAmt('');
      setPayRef('');
      invalidate();
    },
    onError: handleErr,
  });
  const cancelMut = useMutation({
    mutationFn: () => cancelInvoice(invoiceId as number, cancelReason),
    onSuccess: (r) => {
      toast.push(`Invoice ${r.invoiceNo} cancelled`, 'warning');
      setShowCancel(false);
      setCancelReason('');
      invalidate();
      onClose();
    },
    onError: handleErr,
  });

  if (!open) return null;

  return (
    <Modal
      open={open}
      title={inv ? `Invoice ${inv.invoiceNo}` : 'Loading…'}
      onClose={onClose}
      size="xl"
      footer={
        inv ? (
          <>
            <Button variant="glass" onClick={onClose}>
              Close
            </Button>
            <Button
              variant="glass"
              onClick={() => doc.open('sales-invoice', inv.id, inv.invoiceNo, inv.party?.whatsapp ?? inv.party?.mobile ?? '')}
            >
              View Doc
            </Button>
            {inv.status === 'Draft' && !showEdit && canEdit && (
              <Button variant="glass" onClick={() => setShowEdit(true)}>
                Edit
              </Button>
            )}
            {inv.status === 'Draft' && !showEdit && (
              <Button
                variant="primary"
                onClick={() => issueMut.mutate()}
                disabled={issueMut.isPending}
              >
                Issue
              </Button>
            )}
            {(inv.status === 'Issued' || inv.status === 'Partial') && (
              <Button variant="primary" onClick={() => setShowPay(true)}>
                Record Payment
              </Button>
            )}
            {inv.status !== 'Cancelled' && inv.status !== 'Paid' && (
              <Button variant="danger" onClick={() => setShowCancel(true)}>
                Cancel
              </Button>
            )}
          </>
        ) : null
      }
    >
      {query.isLoading && <div className="p-4 text-sm">Loading…</div>}
      {inv && (
        <div className="space-y-2 text-xs sm:space-y-3">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 sm:gap-3">
            <KV label="Status" value={<Badge tone="accent">{inv.status}</Badge>} />
            <KV label="Date" value={formatDate(inv.invoiceDate)} />
            <KV label="Due" value={formatDate(inv.dueDate)} />
            <KV
              label="GST Type"
              value={
                <Badge tone={inv.isInterstate ? 'accent' : 'muted'}>
                  {inv.isInterstate ? 'IGST (inter-state)' : 'CGST+SGST (intra-state)'}
                </Badge>
              }
            />
            <KV label="Customer" value={inv.party?.name ?? '—'} />
            <KV label="GSTIN" value={inv.partyGstin ?? '—'} />
            <KV label="State" value={inv.partyStateCode ?? '—'} />
            <KV label="Payment Terms" value={inv.paymentTerms ?? '—'} />
            <KV label="DO Ref" value={inv.dispatchOrder?.doNo ?? '—'} />
            <KV label="Outward GRN" value={inv.outwardGrn?.grnNo ?? '—'} />
            <KV label="Vehicle" value={inv.vehicleNo ?? '—'} />
            <KV label="LR / E-way" value={`${inv.lrNo ?? '—'} / ${inv.ewayBillNo ?? '—'}`} />
            {inv.irn && <KV label="IRN" value={inv.irn} />}
          </div>

          <div>
            <div className="mb-1 text-[10px] font-bold uppercase text-[var(--text-muted)]">
              Line Items
            </div>
            {/* Mobile stacked cards */}
            <div className="space-y-1.5 sm:hidden">
              {inv.items.map((it) => (
                <div key={it.id} className="rounded-md border border-[var(--border-soft)] p-2">
                  <div className="mb-1 flex items-baseline justify-between gap-2">
                    <div className="min-w-0 flex-1 truncate text-[12px] font-bold text-[var(--text-primary)]">
                      {it.itemName}
                    </div>
                    <div className="flex-shrink-0 text-[12px] font-extrabold text-accent">
                      {formatINR(Number(it.lineTotal))}
                    </div>
                  </div>
                  <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-[11px]">
                    <dt className="text-[10px] font-bold uppercase tracking-wide text-[var(--text-muted)]">HSN</dt>
                    <dd className="text-[var(--text-primary)]">{it.hsn ?? '—'}</dd>
                    <dt className="text-[10px] font-bold uppercase tracking-wide text-[var(--text-muted)]">Qty × Rate</dt>
                    <dd className="text-[var(--text-primary)]">{it.qty} {it.unit} × {formatINR(Number(it.rate))}</dd>
                    {Number(inv.totalDiscount) > 0 && (
                      <>
                        <dt className="text-[10px] font-bold uppercase tracking-wide text-[var(--text-muted)]">Discount</dt>
                        <dd className="text-orange-400">-{formatINR(Number(it.discountAmt))}</dd>
                      </>
                    )}
                    <dt className="text-[10px] font-bold uppercase tracking-wide text-[var(--text-muted)]">Taxable</dt>
                    <dd className="text-[var(--text-primary)]">{formatINR(Number(it.taxableAmt))}</dd>
                    <dt className="text-[10px] font-bold uppercase tracking-wide text-[var(--text-muted)]">GST</dt>
                    <dd className="text-[var(--text-primary)]">
                      {it.gstPct}% ·{' '}
                      {inv.isInterstate
                        ? `IGST ${formatINR(Number(it.igst))}`
                        : `CGST ${formatINR(Number(it.cgst))} + SGST ${formatINR(Number(it.sgst))}`}
                    </dd>
                  </dl>
                </div>
              ))}
            </div>
            {/* Desktop table */}
            <div className="hidden overflow-x-auto rounded-md border border-[var(--border-soft)] sm:block">
              <table className="w-full text-[11px]">
                <thead className="bg-[var(--bg-soft)] text-[10px] uppercase text-[var(--text-muted)]">
                  <tr>
                    <th className="px-2 py-1 text-left">Item</th>
                    <th className="px-2 py-1 text-left">HSN</th>
                    <th className="px-2 py-1 text-right">Qty</th>
                    <th className="px-2 py-1 text-right">Rate</th>
                    <th className="px-2 py-1 text-right">Net Rate</th>
                    {Number(inv.totalDiscount) > 0 && (
                      <th className="px-2 py-1 text-right">Discount</th>
                    )}
                    <th className="px-2 py-1 text-right">Taxable</th>
                    <th className="px-2 py-1 text-right">GST%</th>
                    {inv.isInterstate ? (
                      <th className="px-2 py-1 text-right">IGST</th>
                    ) : (
                      <>
                        <th className="px-2 py-1 text-right">CGST</th>
                        <th className="px-2 py-1 text-right">SGST</th>
                      </>
                    )}
                    <th className="px-2 py-1 text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {inv.items.map((it) => (
                    <tr key={it.id} className="border-t border-[var(--border-soft)]">
                      <td className="px-2 py-1">{it.itemName}</td>
                      <td className="px-2 py-1">{it.hsn ?? '—'}</td>
                      <td className="px-2 py-1 text-right">
                        {it.qty} {it.unit}
                      </td>
                      <td className="px-2 py-1 text-right">{formatINR(Number(it.rate))}</td>
                      <td className="px-2 py-1 text-right font-semibold">
                        {formatINR(Number(it.rate) - (Number(it.qty) ? Number(it.discountAmt) / Number(it.qty) : 0))}
                      </td>
                      {Number(inv.totalDiscount) > 0 && (
                        <td className="px-2 py-1 text-right text-orange-400">
                          -{formatINR(Number(it.discountAmt))}
                        </td>
                      )}
                      <td className="px-2 py-1 text-right">
                        {formatINR(Number(it.taxableAmt))}
                      </td>
                      <td className="px-2 py-1 text-right">{it.gstPct}%</td>
                      {inv.isInterstate ? (
                        <td className="px-2 py-1 text-right">{formatINR(Number(it.igst))}</td>
                      ) : (
                        <>
                          <td className="px-2 py-1 text-right">
                            {formatINR(Number(it.cgst))}
                          </td>
                          <td className="px-2 py-1 text-right">
                            {formatINR(Number(it.sgst))}
                          </td>
                        </>
                      )}
                      <td className="px-2 py-1 text-right font-semibold">
                        {formatINR(Number(it.lineTotal))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex justify-end">
            <table className="text-[11px]">
              <tbody>
                {(inv.discount || inv.specialDiscount) && (
                  <tr>
                    <td className="px-3 py-0.5 text-right text-[10px] text-[var(--text-muted)]">
                      Applied:
                      {inv.discount ? ` Discount ${inv.discount}%` : ''}
                      {inv.discount && inv.specialDiscount ? ' +' : ''}
                      {inv.specialDiscount ? ` Special ${inv.specialDiscount}%` : ''}
                    </td>
                    <td />
                  </tr>
                )}
                <tr>
                  <td className="px-3 py-1 text-right text-[var(--text-muted)]">Gross Total</td>
                  <td className="px-3 py-1 text-right font-semibold">
                    {formatINR(Number(inv.taxableTotal) + Number(inv.totalDiscount))}
                  </td>
                </tr>
                {Number(inv.totalDiscount) > 0 && (
                  <tr>
                    <td className="px-3 py-1 text-right text-[var(--text-muted)]">Discount</td>
                    <td className="px-3 py-1 text-right font-semibold text-orange-400">
                      -{formatINR(Number(inv.totalDiscount))}
                    </td>
                  </tr>
                )}
                <tr>
                  <td className="px-3 py-1 text-right text-[var(--text-muted)]">Taxable Total</td>
                  <td className="px-3 py-1 text-right font-semibold">
                    {formatINR(Number(inv.taxableTotal))}
                  </td>
                </tr>
                {inv.isInterstate ? (
                  <tr>
                    <td className="px-3 py-1 text-right text-[var(--text-muted)]">IGST</td>
                    <td className="px-3 py-1 text-right font-semibold">
                      {formatINR(Number(inv.totalIgst))}
                    </td>
                  </tr>
                ) : (
                  <>
                    <tr>
                      <td className="px-3 py-1 text-right text-[var(--text-muted)]">CGST</td>
                      <td className="px-3 py-1 text-right font-semibold">
                        {formatINR(Number(inv.totalCgst))}
                      </td>
                    </tr>
                    <tr>
                      <td className="px-3 py-1 text-right text-[var(--text-muted)]">SGST</td>
                      <td className="px-3 py-1 text-right font-semibold">
                        {formatINR(Number(inv.totalSgst))}
                      </td>
                    </tr>
                  </>
                )}
                <tr className="border-t border-[var(--border-color)]">
                  <td className="px-3 py-1 text-right font-bold">Grand Total</td>
                  <td className="px-3 py-1 text-right text-base font-extrabold text-accent">
                    {formatINR(Number(inv.grandTotal))}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {showEdit && inv.status === 'Draft' && (
            <InvoiceEditPanel
              key={inv.id}
              inv={inv}
              onClose={() => setShowEdit(false)}
              onDone={() => {
                setShowEdit(false);
                invalidate();
              }}
            />
          )}

          {showPay && (
            <div className="rounded-md border border-[var(--border-soft)] bg-[var(--bg-soft)] p-3">
              <div className="mb-2 text-[10px] font-bold uppercase text-[var(--text-muted)]">
                Record Payment
              </div>
              <div className="grid grid-cols-3 gap-2">
                <FormGroup label="Amount">
                  <Input
                    type="number"
                    value={payAmt}
                    onChange={(e) => setPayAmt(e.target.value)}
                  />
                </FormGroup>
                <FormGroup label="Mode">
                  <Select value={payMode} onChange={(e) => setPayMode(e.target.value)}>
                    <option>NEFT</option>
                    <option>RTGS</option>
                    <option>UPI</option>
                    <option>Cheque</option>
                    <option>Cash</option>
                  </Select>
                </FormGroup>
                <FormGroup label="Reference">
                  <Input value={payRef} onChange={(e) => setPayRef(e.target.value)} />
                </FormGroup>
              </div>
              <div className="mt-2 flex justify-end gap-2">
                <Button variant="glass" size="sm" onClick={() => setShowPay(false)}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => payMut.mutate()}
                  disabled={!payAmt || payMut.isPending}
                >
                  Save Payment
                </Button>
              </div>
            </div>
          )}

          {showCancel && (
            <div className="rounded-md border border-rose-300 bg-rose-50 p-3">
              <div className="mb-2 text-[10px] font-bold uppercase text-rose-700">
                Cancel Invoice
              </div>
              <FormGroup label="Reason">
                <Textarea
                  rows={2}
                  autoFocus
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                />
              </FormGroup>
              <div className="mt-2 flex justify-end gap-2">
                <Button variant="glass" size="sm" onClick={() => setShowCancel(false)}>
                  Back
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => cancelMut.mutate()}
                  disabled={!cancelReason.trim() || cancelMut.isPending}
                >
                  Confirm Cancel
                </Button>
              </div>
            </div>
          )}

          <InvoiceAttachments invoiceId={inv.id} />
        </div>
      )}
    </Modal>
  );
}

function KV({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div className="text-[10px] uppercase text-[var(--text-muted)]">{label}</div>
      <div className="text-[12px] font-semibold">{value}</div>
    </div>
  );
}
